import { expValEquals } from '@confluence/feature-experiments';

import type { QualifyingProduct } from './thirdPartyPromptTypes.ts';
import { useThirdPartyPromptProduct } from './useThirdPartyPromptProduct.ts';
import { useThirdPartyPromptState } from './useThirdPartyPromptState.ts';

export type ThirdPartyPromptPlacement = 'none' | 'first' | 'holding';

export type ThirdPartyPromptPlacementResult = {
	placement: ThirdPartyPromptPlacement;
	product: QualifyingProduct | undefined;
	isResolved: boolean;
};

/**
 * Whether the related-content grid should prepend the connect prompt and drop the lowest-ranked rec.
 * `none` leaves the rec list unchanged. `holding` waits for this page's outgoing links, the same
 * gate SSR uses. `first` is only for a page candidate (`single` / `many`); the skeleton does not
 * wait on the ADF walk or card icons.
 *
 * Callers must already have passed the `shouldEvaluate` kill switch. This hook only decides
 * treatment vs control, and only exposes when a prompt would actually show.
 */
export const useThirdPartyPromptPlacement = (entityId: string): ThirdPartyPromptPlacementResult => {
	const { state } = useThirdPartyPromptState(entityId);
	const { product, isResolved } = useThirdPartyPromptProduct(state, entityId);

	if (state.kind === 'pending') {
		return { placement: 'holding', product, isResolved };
	}

	const shouldReserveSlot = state.kind !== 'none';
	if (!shouldReserveSlot) {
		return { placement: 'none', product, isResolved };
	}

	return {
		placement: expValEquals('confluence_3p_in_related_content', 'isEnabled', true)
			? 'first'
			: 'none',
		product,
		isResolved,
	};
};
